import React from 'react';
import { 
  Box, Typography, Card, CardContent, Grid, Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { theme } from '../../helpers/theme';

const DocumentationTab = () => {
  const apiEndpoints = [
    {
      title: 'Chat Generation',
      method: 'POST',
      endpoint: '/api/generate',
      description: 'Generate AI responses for chat conversations',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        query: 'string (required) - The user\'s question or prompt',
        model: 'string (required) - The model to use for generation',
        domain: 'string (optional, default: "main") - The domain context'
      },
      response: 'Streaming text response'
    },
    {
      title: 'Generate Query',
      method: 'POST',
      endpoint: '/api/query',
      description: 'Generate structured query responses',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        query: 'string (required) - The user\'s question or prompt',
        model: 'string (required) - The model to use for generation',
        domain: 'string (optional, default: "main") - The domain context'
      },
      response: 'JSON response with query results'
    },
    {
      title: 'Create Chat Session',
      method: 'POST',
      endpoint: '/api/session',
      description: 'Create a new chat session for persistent conversations',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        title: 'string (optional, default: "New Chat") - The session title'
      },
      response: 'JSON object with session_id and session details'
    },
    {
      title: 'Get All Sessions',
      method: 'GET',
      endpoint: '/api/session',
      description: 'Retrieve all chat sessions',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON array of session objects'
    },
    {
      title: 'Get Session Messages',
      method: 'GET',
      endpoint: '/api/session/{sessionId}?page=1&limit=50',
      description: 'Get messages for a specific chat session with pagination',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON object with messages array and pagination info'
    },
    {
      title: 'Send Chat Message',
      method: 'POST',
      endpoint: '/api/chat',
      description: 'Send a message within an existing chat session for contextual conversations with support for audio files',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        session: 'string (required) - The session ID from createSession',
        message: 'string (required) - The user message',
        model: 'string (required) - The model to use for generation',
        domain: 'string (optional, default: "main") - The domain context',
        skip_rag: 'boolean (optional, default: false) - Skip RAG processing',
        audio: 'file (optional) - Audio file to be processed with the message'
      },
      response: 'Server-Sent Events (SSE) streaming response',
      note: 'This endpoint uses FormData instead of JSON. Audio files supported: mp3, wav, ogg, m4a, flac, webm'
    },
    {
      title: 'Message Feedback',
      method: 'PATCH',
      endpoint: '/api/message',
      description: 'Provide feedback (thumbs up/down) for AI responses to improve quality',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        message_id: 'string (required) - The ID of the message to rate',
        is_good: 'boolean (required) - true for positive feedback, false for negative'
      },
      response: 'JSON confirmation of feedback submission'
    },
    {
      title: 'File Upload & Ingestion',
      method: 'POST',
      endpoint: '/api/file/ingest',
      description: 'Upload and process documents for RAG (Retrieval Augmented Generation)',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        files: 'file[] (required) - Files to upload and process',
        domain: 'string (optional, default: "main") - The domain context'
      },
      response: 'JSON with ingestion status and file IDs'
    },
    {
      title: 'Website Crawling',
      method: 'POST',
      endpoint: '/api/file/crawl',
      description: 'Crawl and ingest content from websites for RAG processing',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        url: 'string (required) - The URL to crawl',
        domain: 'string (optional, default: "main") - The domain context',
        max_page: 'number (optional, default: 100) - Maximum pages to crawl',
        max_depth: 'number (optional, default: 3) - Maximum crawl depth',
        crawl_sub_domain: 'boolean (optional, default: true) - Whether to crawl subdomains'
      },
      response: 'JSON with crawling status and upload ID'
    },
    {
      title: 'Check Upload Status',
      method: 'GET',
      endpoint: '/api/file/upload/{uploadId}',
      description: 'Check the status of file upload or crawling operation',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON object with upload/crawl status and progress'
    },
    {
      title: 'List Files',
      method: 'GET',
      endpoint: '/api/file?domain=main&page=1&limit=30',
      description: 'Retrieve a list of uploaded files with pagination',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON array of file objects with metadata'
    },
    {
      title: 'Get File Details',
      method: 'GET',
      endpoint: '/api/file/{fileId}',
      description: 'Get detailed information about a specific file',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON object with file details and content'
    },
    {
      title: 'Delete File',
      method: 'DELETE',
      endpoint: '/api/file/{fileId}',
      description: 'Remove a file from the system',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        domain: 'string (optional, default: "main") - The domain context'
      },
      response: 'JSON confirmation of deletion'
    },
    {
      title: 'List Available Models',
      method: 'GET',
      endpoint: '/api/models',
      description: 'Get all available AI models for this endpoint',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON array of available models'
    },
    {
      title: 'Models Health Check',
      method: 'GET',
      endpoint: '/api/models/health',
      description: 'Check the health status of all available models',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON object with model health statuses'
    },
    {
      title: 'System Health Check',
      method: 'GET',
      endpoint: '/api/health',
      description: 'Check the overall health status of the system and services',
      headers: [
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      response: 'JSON object with system health status and service details'
    },
    {
      title: 'Validate API Key',
      method: 'POST',
      endpoint: '/api/key/validate',
      description: 'Validate an API key',
      headers: [
        { name: 'Content-Type', value: 'application/json', required: true },
        { name: 'x-api-key', value: 'YOUR_API_KEY', required: true }
      ],
      body: {
        key: 'string (required) - The API key to validate'
      },
      response: 'JSON with validation status'
    }
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
        API Documentation
      </Typography>
      
      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4 }}>
        Use these APIs to integrate AI capabilities into your applications. All endpoints require authentication via API key.
      </Typography>

      <Card sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mb: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            ⚠️ Important: Base URL Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            All API calls must use your agent's specific endpoint as the base URL:
          </Typography>
          <Box sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: 1, 
            p: 2, 
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            mb: 2
          }}>
            <Typography sx={{ color: '#4fc3f7' }}>
              Base URL: https://YOUR_AGENT_ENDPOINT/api
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Replace "YOUR_AGENT_ENDPOINT" with your actual agent endpoint (found in the Infrastructure section above).
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {apiEndpoints.map((api, index) => (
          <Grid item xs={12} key={index}>
            <Accordion 
              sx={{ 
                backgroundColor: theme.palette.background.paper,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Chip
                    label={api.method}
                    size="small"
                    sx={{
                      backgroundColor: 
                        api.method === 'GET' ? '#4caf50' :
                        api.method === 'POST' ? '#2196f3' :
                        api.method === 'DELETE' ? '#f44336' : 
                        api.method === 'PATCH' ? '#ff9800' : '#9c27b0',
                      color: 'white',
                      fontWeight: 600,
                      minWidth: 70
                    }}
                  />
                  <Typography sx={{ color: 'white', fontWeight: 500, fontFamily: 'monospace' }}>
                    {api.endpoint}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 'auto' }}>
                    {api.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3 }}>
                  {api.description}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 2, fontSize: '1rem' }}>
                    Full URL
                  </Typography>
                  <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: 1, p: 2, mb: 2 }}>
                    <Typography sx={{ color: '#4fc3f7', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      https://YOUR_AGENT_ENDPOINT{api.endpoint}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 2, fontSize: '1rem' }}>
                    Headers
                  </Typography>
                  <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: 1, p: 2 }}>
                    {api.headers.map((header, idx) => (
                      <Box key={idx} sx={{ display: 'flex', mb: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        <Typography sx={{ color: '#ce93d8', minWidth: 120 }}>
                          {header.name}:
                        </Typography>
                        <Typography sx={{ color: '#f8bbd9', mr: 2 }}>
                          {header.value}
                        </Typography>
                        {header.required && (
                          <Chip label="Required" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#f44336', color: 'white' }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {api.body && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2, fontSize: '1rem' }}>
                      Request Body
                    </Typography>
                    <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: 1, p: 2 }}>
                      {Object.entries(api.body).map(([key, value]) => (
                        <Box key={key} sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          <Typography component="span" sx={{ color: '#81c784' }}>
                            "{key}":
                          </Typography>
                          <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 1 }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography variant="h6" sx={{ color: 'white', mb: 2, fontSize: '1rem' }}>
                    Response
                  </Typography>
                  <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', borderRadius: 1, p: 2 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {api.response}
                    </Typography>
                  </Box>
                </Box>

                {api.note && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2, fontSize: '1rem' }}>
                      Important Note
                    </Typography>
                    <Box sx={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1, p: 2, border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.85rem' }}>
                        ⚠️ {api.note}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mt: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Chat Session Workflow
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            For persistent conversations with context, follow this workflow:
          </Typography>
          <Box sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: 1, 
            p: 2, 
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}>
            <Typography sx={{ color: '#4fc3f7', mb: 1 }}>
              1. Create Session: POST https://YOUR_AGENT_ENDPOINT/api/session
            </Typography>
            <Typography sx={{ color: '#81c784', mb: 1 }}>
              2. Send Messages: POST https://YOUR_AGENT_ENDPOINT/api/chat (with session ID)
            </Typography>
            <Typography sx={{ color: '#ffb74d', mb: 1 }}>
              3. Retrieve Sessions: GET https://YOUR_AGENT_ENDPOINT/api/session
            </Typography>
            <Typography sx={{ color: '#ce93d8' }}>
              4. Provide Feedback: PATCH https://YOUR_AGENT_ENDPOINT/api/message (optional)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mt: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Website Crawling Workflow
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            For crawling and ingesting website content:
          </Typography>
          <Box sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: 1, 
            p: 2, 
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}>
            <Typography sx={{ color: '#4fc3f7', mb: 1 }}>
              1. Start Crawl: POST https://YOUR_AGENT_ENDPOINT/api/file/crawl
            </Typography>
            <Typography sx={{ color: '#81c784', mb: 1 }}>
              2. Check Status: GET https://YOUR_AGENT_ENDPOINT/api/file/upload/{"{"}{`uploadId`}{"}"}
            </Typography>
            <Typography sx={{ color: '#ffb74d' }}>
              3. List Files: GET https://YOUR_AGENT_ENDPOINT/api/file (when complete)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mt: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Example Integration
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            Complete example using your agent's endpoint:
          </Typography>
          <Box sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: 1, 
            p: 2, 
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}>
            <Typography sx={{ color: '#4fc3f7', fontSize: '0.85rem', lineHeight: 1.6 }}>
              <span style={{ color: '#81c784' }}>curl</span>{' '}
              <span style={{ color: '#ffb74d' }}>-X POST</span>{' '}
              <span style={{ color: '#f8bbd9' }}>"https://YOUR_AGENT_ENDPOINT/api/generate"</span>{' '}
              \<br />
              &nbsp;&nbsp;<span style={{ color: '#ffb74d' }}>-H</span>{' '}
              <span style={{ color: '#f8bbd9' }}>"Content-Type: application/json"</span>{' '}
              \<br />
              &nbsp;&nbsp;<span style={{ color: '#ffb74d' }}>-H</span>{' '}
              <span style={{ color: '#f8bbd9' }}>"x-api-key: YOUR_API_KEY"</span>{' '}
              \<br />
              &nbsp;&nbsp;<span style={{ color: '#ffb74d' }}>-d</span>{' '}
              <span style={{ color: '#f8bbd9' }}>'{"{"}</span><br />
              &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#ce93d8' }}>"query"</span>: <span style={{ color: '#f8bbd9' }}>"Your question here"</span>,<br />
              &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#ce93d8' }}>"model"</span>: <span style={{ color: '#f8bbd9' }}>"your-model-name"</span>,<br />
              &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#ce93d8' }}>"domain"</span>: <span style={{ color: '#f8bbd9' }}>"main"</span><br />
              &nbsp;&nbsp;<span style={{ color: '#f8bbd9' }}>{"}"}'</span>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ 
        backgroundColor: theme.palette.background.paper,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        mt: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Authentication
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            All API requests require authentication using your API key in the request header:
          </Typography>
          <Box sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.3)', 
            borderRadius: 1, 
            p: 2, 
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}>
            <Typography sx={{ color: '#ce93d8' }}>
              x-api-key: YOUR_API_KEY_HERE
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentationTab; 